// features/projects/templates/continuum/questions.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM OPEN QUESTIONS — known-unknowns register
//
// Captures questions surfaced during research that aren't claims
// yet. Distinct from gaps (missing data) and refuters (bounded
// falsifiers) — these are deeper unknowns about the model itself.
//
// Sections by status: OPEN -> INVESTIGATING -> ANSWERED -> PARKED.
// Inline add form at the top. Each question can transition status
// and capture an answer when answered.
//
// Session 8 of the Continuum-actionability extension.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { C, FONT as F } from './tokens';
import { DYNAMICS } from '../../../../lib/continuum/simulation/methodology';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

const STATUS_COLOR = {
  open:          C.amber,
  investigating: C.gold,
  answered:      C.sage,
  parked:        C.dim,
};

const STATUS_LABEL = {
  open:          'OPEN',
  investigating: 'INVESTIGATING',
  answered:      'ANSWERED',
  parked:        'PARKED',
};

const PRIORITY_COLOR = {
  high:   C.terra,
  medium: C.gold,
  low:    C.dim,
};

const STATUS_ORDER = ['open', 'investigating', 'answered', 'parked'];
const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

export default function ContinuumQuestions({ workspaceId }) {
  const [questions, setQuestions] = useState(null);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setError(null);
    try {
      const r = await fetch(`/api/continuum/questions/list?workspace_id=${workspaceId}`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setQuestions(j.questions || []);
    } catch (err) {
      setError(err.message);
      setQuestions([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const loading = questions === null;
  const grouped = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = (questions || []).filter(q => q.status === s)
      .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99));
    return acc;
  }, {});
  const counts = Object.fromEntries(STATUS_ORDER.map(s => [s, grouped[s].length]));

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      fontFamily: F.mono, fontSize: 12,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Header
        loading={loading}
        onReload={reload}
        counts={counts}
        onAdd={() => setAddOpen(true)}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 22px' }}>
        {addOpen && (
          <AddQuestionForm
            workspaceId={workspaceId}
            onCancel={() => setAddOpen(false)}
            onAdded={() => { setAddOpen(false); reload(); }}
          />
        )}
        {loading ? (
          <Empty msg="Loading questions…" />
        ) : error ? (
          <Empty msg={`Error: ${error}`} tone="error" />
        ) : (questions || []).length === 0 && !addOpen ? (
          <Empty msg="No open questions yet. Click + Add to register an unknown surfaced during research." />
        ) : (
          <>
            {STATUS_ORDER.map(status => grouped[status].length > 0 && (
              <Section key={status} title={`${STATUS_LABEL[status]} (${grouped[status].length})`} accent={STATUS_COLOR[status]}>
                {grouped[status].map(q => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    workspaceId={workspaceId}
                    onChanged={reload}
                  />
                ))}
              </Section>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────
// Matches features/projects/templates/continuum/findings.js Header
// pattern: pill label + context line + action buttons.
function Header({ loading, onReload, counts, onAdd }) {
  const accent = C.amber;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 42,
      borderBottom: `1px solid ${C.b1}`, background: C.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 9, padding: '3px 10px', borderRadius: 4,
        background: 'rgba(212,149,106,.10)', border: '1px solid rgba(212,149,106,.25)',
        color: accent,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
        Questions — Open Unknowns
      </div>
      <div style={{ fontSize: 9, color: C.dim }}>
        {STATUS_ORDER.map(s => `${counts[s]} ${STATUS_LABEL[s].toLowerCase()}`).join(' · ')}
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        <button
          onClick={onAdd}
          style={{
            fontSize: 9, padding: '5px 14px', borderRadius: 4,
            background: 'transparent', border: `1px solid ${accent}`,
            color: accent, cursor: 'pointer',
            fontFamily: F.mono, letterSpacing: '0.06em',
          }}
        >
          + Add question
        </button>
        <button
          onClick={onReload}
          disabled={loading}
          style={{
            fontSize: 9, padding: '5px 14px', borderRadius: 4,
            background: 'transparent', border: `1px solid ${C.b2}`,
            color: C.static, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: F.mono, letterSpacing: '0.06em',
          }}
        >
          {loading ? '⟳ Loading' : '↻ Refresh'}
        </button>
      </div>
    </div>
  );
}

function Empty({ msg, tone }) {
  return (
    <div style={{
      padding: '40px 20px', textAlign: 'center',
      color: tone === 'error' ? C.terra : C.dim,
      fontFamily: F.sans, fontSize: 13, lineHeight: 1.6,
      maxWidth: 600, margin: '40px auto',
    }}>
      {msg}
    </div>
  );
}

function Section({ title, accent, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        fontFamily: F.mono, fontSize: 9, letterSpacing: '0.16em',
        color: accent, textTransform: 'uppercase', fontWeight: 600,
        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.b1}`,
      }}>
        ◆ {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Add form ─────────────────────────────────────────────────
function AddQuestionForm({ workspaceId, onCancel, onAdded }) {
  const [question, setQuestion] = useState('');
  const [context, setContext] = useState('');
  const [priority, setPriority] = useState('medium');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!question.trim() || question.trim().length < 5) {
      setError('Question must be at least 5 characters');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await fetch('/api/continuum/questions/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          question: question.trim(),
          context: context.trim() || null,
          priority,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      onAdded?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      marginBottom: 24, padding: '14px',
      background: 'rgba(200,149,106,.04)',
      border: `1px solid ${C.amber}40`, borderRadius: 5,
    }}>
      <div style={{
        fontFamily: F.mono, fontSize: 9, letterSpacing: '0.16em',
        color: C.amber, textTransform: 'uppercase', marginBottom: 10, fontWeight: 600,
      }}>
        ◆ New open question
      </div>

      <Field label="Question">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={2}
          placeholder="State the unknown as a question."
          style={INPUT_STYLE}
        />
      </Field>

      <div style={{ marginTop: 10 }}>
        <Field label="Context (why does this matter?)">
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={2}
            placeholder="What surfaced this question? What does an answer unlock?"
            style={INPUT_STYLE}
          />
        </Field>
      </div>

      <div style={{ marginTop: 10 }}>
        <Field label="Priority">
          <div style={{ display: 'flex', gap: 6 }}>
            {['high', 'medium', 'low'].map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPriority(p)}
                style={{
                  flex: 1, padding: '6px 10px',
                  background: priority === p ? `${PRIORITY_COLOR[p]}20` : 'transparent',
                  border: `1px solid ${priority === p ? PRIORITY_COLOR[p] : C.b2}`,
                  borderRadius: 3,
                  color: priority === p ? PRIORITY_COLOR[p] : C.mid,
                  fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
                  textTransform: 'uppercase', cursor: 'pointer', fontWeight: 600,
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </Field>
      </div>

      {error && (
        <div style={{
          marginTop: 10, padding: '6px 10px',
          background: 'rgba(196,80,80,.08)',
          border: '1px solid rgba(196,80,80,.40)',
          borderRadius: 3, fontSize: 11, color: C.terra, fontFamily: F.mono,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        <button
          type="button" onClick={onCancel} disabled={saving}
          style={BUTTON_SECONDARY}
        >
          Cancel
        </button>
        <button
          type="button" onClick={submit} disabled={saving}
          style={{ ...BUTTON_PRIMARY, background: C.amber, borderColor: C.amber }}
        >
          {saving ? 'Saving…' : 'Add question'}
        </button>
      </div>
    </div>
  );
}

// ─── Question card ────────────────────────────────────────────
function QuestionCard({ question, workspaceId, onChanged }) {
  const [transitionOpen, setTransitionOpen] = useState(false);
  const statusColor = STATUS_COLOR[question.status] || C.dim;
  const priColor = PRIORITY_COLOR[question.priority] || C.dim;
  const dyn = DYN_BY_KEY[question.comparison?.dynamic_key];

  return (
    <div style={{
      padding: '12px 14px',
      background: 'rgba(255,255,255,.02)',
      border: `1px solid ${C.b1}`, borderRadius: 5,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 10, fontFamily: F.mono, fontSize: 9,
      }}>
        <span style={{
          padding: '2px 8px', borderRadius: 3,
          border: `1px solid ${statusColor}60`,
          background: `${statusColor}15`, color: statusColor,
          letterSpacing: '0.08em', fontWeight: 600, textTransform: 'uppercase',
        }}>
          {STATUS_LABEL[question.status]}
        </span>
        <span style={{
          padding: '1px 6px', borderRadius: 2,
          border: `1px solid ${priColor}40`,
          color: priColor, letterSpacing: '0.04em',
        }}>
          {question.priority}
        </span>
        {dyn && (
          <span style={{ color: C.mid, letterSpacing: '0.04em' }}>
            {dyn.label}
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: C.dim, letterSpacing: '0.04em' }}>
          {question.status === 'answered' && question.answered_at
            ? `answered ${formatDate(question.answered_at)}`
            : `asked ${formatDate(question.created_at)}`}
        </span>
      </div>

      <div style={{
        fontSize: 12, color: C.text, lineHeight: 1.5, fontFamily: F.sans,
        whiteSpace: 'pre-wrap',
      }}>
        {question.question}
      </div>

      {(question.hypothesis || question.prediction) && (
        <div style={{
          marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          {question.hypothesis && (
            <LinkBadge
              kind="hypothesis"
              accent={C.teal}
              label="Tied to parallel hypothesis"
              text={question.hypothesis.eco_claim}
              status={question.hypothesis.status}
            />
          )}
          {question.prediction && (
            <LinkBadge
              kind="prediction"
              accent={C.sage}
              label="Tied to forward prediction"
              text={question.prediction.claim}
              status={question.prediction.status}
            />
          )}
        </div>
      )}

      {question.context && (
        <div style={{
          marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.b1}`,
          fontSize: 11, color: C.mid, lineHeight: 1.5, fontFamily: F.sans,
          whiteSpace: 'pre-wrap',
        }}>
          {question.context}
        </div>
      )}

      {question.answer_note && (
        <div style={{
          marginTop: 10, padding: '8px 10px',
          background: `${C.sage}08`,
          border: `1px solid ${C.sage}30`, borderRadius: 4,
        }}>
          <div style={{
            fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
            color: C.sage, textTransform: 'uppercase', marginBottom: 4,
          }}>
            Answer
          </div>
          <div style={{
            fontSize: 11, color: C.mid, lineHeight: 1.5, fontFamily: F.sans,
            whiteSpace: 'pre-wrap',
          }}>
            {question.answer_note}
          </div>
        </div>
      )}

      {question.status !== 'answered' && (
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
          {!transitionOpen ? (
            <button
              type="button"
              onClick={() => setTransitionOpen(true)}
              style={{
                background: 'transparent', border: `1px solid ${C.b2}`, borderRadius: 3,
                padding: '5px 14px', fontFamily: F.mono, fontSize: 10,
                letterSpacing: '0.06em', color: C.dim, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Update
            </button>
          ) : (
            <TransitionForm
              question={question}
              workspaceId={workspaceId}
              onCancel={() => setTransitionOpen(false)}
              onUpdated={() => { setTransitionOpen(false); onChanged?.(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Transition form ──────────────────────────────────────────
function TransitionForm({ question, workspaceId, onCancel, onUpdated }) {
  const allowed = {
    open:          ['investigating', 'answered', 'parked'],
    investigating: ['answered', 'parked', 'open'],
    parked:        ['open', 'investigating', 'answered'],
  }[question.status] || [];

  const [chosen, setChosen] = useState(null);
  const [answerNote, setAnswerNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const needsAnswer = chosen === 'answered';

  const submit = async () => {
    if (!chosen || saving) return;
    if (needsAnswer && !answerNote.trim()) {
      setError('Answer note required when marking as Answered');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        workspace_id: workspaceId,
        question_id: question.id,
        status: chosen,
      };
      if (needsAnswer) body.answer_note = answerNote.trim();
      const r = await fetch('/api/continuum/questions/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      onUpdated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      width: '100%', padding: '12px',
      background: 'rgba(200,169,110,.04)',
      border: `1px solid ${C.gold}40`, borderRadius: 4,
    }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.gold, textTransform: 'uppercase', marginBottom: 10, fontWeight: 600,
      }}>
        ◆ Update status
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {allowed.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => setChosen(s)}
            style={{
              flex: 1,
              padding: '6px 10px',
              background: chosen === s ? `${STATUS_COLOR[s]}20` : 'transparent',
              border: `1px solid ${chosen === s ? STATUS_COLOR[s] : C.b2}`,
              borderRadius: 3,
              color: chosen === s ? STATUS_COLOR[s] : C.mid,
              fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
              textTransform: 'uppercase', cursor: 'pointer', fontWeight: 600,
              minWidth: 100,
            }}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {needsAnswer && (
        <label style={{ display: 'block' }}>
          <span style={{
            display: 'block',
            fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
            color: C.dim, textTransform: 'uppercase', marginBottom: 4,
          }}>
            Answer (required)
          </span>
          <textarea
            value={answerNote}
            onChange={(e) => setAnswerNote(e.target.value)}
            rows={3}
            placeholder="What did you find? Cite specific data, dates, sources."
            style={INPUT_STYLE}
          />
        </label>
      )}

      {error && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: 'rgba(196,80,80,.08)',
          border: '1px solid rgba(196,80,80,.40)',
          borderRadius: 3, fontSize: 11, color: C.terra, fontFamily: F.mono,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
        <button
          type="button" onClick={onCancel} disabled={saving}
          style={BUTTON_SECONDARY}
        >
          Cancel
        </button>
        <button
          type="button" onClick={submit}
          disabled={!chosen || saving}
          style={{
            ...BUTTON_PRIMARY,
            background: chosen ? C.gold : C.b2,
            borderColor: chosen ? C.gold : C.b2,
            color: chosen ? '#1a1a1a' : C.dim,
            cursor: !chosen || saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Apply'}
        </button>
      </div>
    </div>
  );
}

// ─── Field wrapper + shared button styles ─────────────────────
function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{
        display: 'block',
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
        color: C.dim, textTransform: 'uppercase', marginBottom: 4,
      }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const INPUT_STYLE = {
  width: '100%',
  background: 'rgba(255,255,255,.02)',
  border: `1px solid ${C.b1}`, borderRadius: 3,
  padding: '6px 8px', fontFamily: 'inherit', fontSize: 12,
  color: C.text, resize: 'vertical', outline: 'none',
  boxSizing: 'border-box',
};

const BUTTON_SECONDARY = {
  background: 'transparent', border: `1px solid ${C.b1}`, borderRadius: 3,
  padding: '6px 14px', fontFamily: F.mono, fontSize: 10,
  letterSpacing: '0.06em', color: C.dim, textTransform: 'uppercase',
  cursor: 'pointer',
};

const BUTTON_PRIMARY = {
  background: C.gold, border: `1px solid ${C.gold}`, borderRadius: 3,
  padding: '6px 14px', fontFamily: F.mono, fontSize: 10,
  letterSpacing: '0.06em', color: '#1a1a1a',
  textTransform: 'uppercase', cursor: 'pointer', fontWeight: 600,
};

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}

// ─── Cross-reference badge ────────────────────────────────────
// Renders a small inline strip showing that a question is tied to
// a parallel hypothesis or forward prediction. Truncates the claim
// preview so the badge stays a single visual unit; full content is
// in the related tab.
function LinkBadge({ kind, accent, label, text, status }) {
  const truncated = String(text || '').replace(/\s+/g, ' ').trim();
  const preview = truncated.length > 120 ? truncated.slice(0, 119) + '…' : truncated;
  return (
    <div style={{
      padding: '5px 10px',
      background: `${accent}10`,
      border: `1px solid ${accent}40`,
      borderRadius: 3,
      display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
      fontSize: 10, fontFamily: F.mono, color: accent,
      letterSpacing: '0.04em',
    }}>
      <span style={{ whiteSpace: 'nowrap' }}>↳ {label}</span>
      {status && (
        <span style={{
          padding: '0 5px', borderRadius: 2,
          border: `1px solid ${accent}60`,
          fontSize: 8, letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
          {status}
        </span>
      )}
      <span style={{
        flex: 1, fontFamily: F.sans, fontSize: 11, color: C.mid,
        letterSpacing: 'normal', lineHeight: 1.4,
      }}>
        {preview}
      </span>
    </div>
  );
}
